import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, DatePicker, Input, Space, Table } from 'antd';
import {
	CloseCircleOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	PlusCircleOutlined,
	SearchOutlined,
} from '@ant-design/icons';
import { gold, green, purple, red } from '@ant-design/colors';
import Page from '../../components/page/page';
import { PhoneLink } from 'components';
import { useOrders, useUserLevel } from '../../reducers/index';
import { Formatting } from '../../utils';
import styled from 'styled-components';
import moment from 'moment';

import '../../styles/pages/orders/orders.scss';
import { Order, OrderState, UserLevel } from 'types';
import { ColumnsType } from 'antd/lib/table';

const OrdersTable = styled.div`
	flex: 1;
	height: 1px;

	> .ant-table-wrapper {
		height: 100%;

		.ant-table-row {
			&.red {
				> td:first-child {
					border-left: 8px solid ${red[5]};
				}
			}

			&.yellow {
				> td:first-child {
					border-left: 8px solid ${gold[5]};
				}
			}

			&.green {
				> td:first-child {
					border-left: 8px solid ${green[5]};
				}
			}

			&.purple {
				> td:first-child {
					border-left: 8px solid ${purple[5]};
				}
			}
		}
	}
`;

export const Orders: React.FC = () => {
	const navigate = useNavigate();

	const [search, setSearch] = useState<string>();
	const [clientId, setClientId] = useState<number>();
	const [after, setAfter] = useState(moment().startOf('month'));
	const [before, setBefore] = useState(moment().endOf('month'));
	const [done, setDone] = useState(false);

	const userLevel = useUserLevel();
	const [orders] = useOrders({
		after,
		before,
		clientId,
		search,
		done: done ? undefined : false,
	});

	const columns: ColumnsType<Order> = [
		{
			title: 'Klient',
			dataIndex: 'client',
			key: 'name',
			render: (_, order) => {
				const isAdmin = (userLevel ?? 0) >= UserLevel.admin;
				if (!isAdmin) {
					return order.client.name;
				}

				return <Link to={`/panel/clients/${order.client.id}`}>{order.client.name}</Link>;
			},
		},
		{
			title: 'Telefon',
			dataIndex: 'client',
			key: 'phone',
			render: (_, order) => <PhoneLink phone={order.client.phone} />,
		},
		{
			title: 'Adres',
			dataIndex: 'address',
			key: 'address',
			render: (_, order) => order.address,
		},
		{
			title: 'Podstawienie',
			dataIndex: 'deliveryDate',
			key: 'date',
			render: (_, order) => {
				if (!order.deliveryDate) {
					return '-';
				}

				return Formatting.epochToDate(order.deliveryDate, moment(order.deliveryDate).hours() === 0);
			},
		},
		{
			title: 'Odbiór',
			dataIndex: 'claimingDate',
			key: 'claiming',
			render: (_, order) => {
				if (!order.claimingDate) {
					return '-';
				}

				return Formatting.epochToDate(order.claimingDate, moment(order.claimingDate).hours() === 0);
			},
		},
		{
			title: 'Typ odpadów',
			dataIndex: 'wasteType',
			key: 'wasteType',
			render: (_, order) => order.wasteType,
		},
		{
			title: 'Akcje',
			dataIndex: 'actions',
			key: 'actions',
			render: (_, order) => <Link to={`/panel/orders/${order.id}/${order.type}`}>Szczegóły</Link>,
		},
	];

	const getRowClassName = (order: Order) => {
		switch (order.state) {
			case OrderState.inProgress:
				return '';

			case OrderState.done:
				return 'red';

			case OrderState.paid:
				return 'yellow';

			case OrderState.invoiced:
				return 'green';
		}
	};

	const actions = [
		<Button
			key="0"
			size="large"
			type="primary"
			icon={<PlusCircleOutlined />}
			onClick={() => navigate('/panel/new-order')}
		>
			Dodaj zamówienie
		</Button>,
		<Button
			key="1"
			size="large"
			type="primary"
			icon={done ? <EyeInvisibleOutlined /> : <EyeOutlined />}
			onClick={() => setDone((d) => !d)}
		>
			{done ? 'Ukryj' : 'Pokaż'} zamknięte zamówienia
		</Button>,
	];

	const onSearchChange = (search: string) => {
		const s = search.trim();
		if (s === '') {
			setSearch(undefined);
		} else {
			setSearch(s);
		}
	};

	return (
		<Page title="Zamówienia" actions={actions}>
			<div className="orders flex flex-dir">
				<div className="inputs flex flex-dir">
					<Input
						size="large"
						type="text"
						placeholder="Wyszukaj"
						suffix={<SearchOutlined />}
						value={search}
						onChange={(e) => onSearchChange(e.target.value)}
					/>
					<Space size={25}>
						<DatePicker size="large" value={after ?? null} onChange={(m) => m && setAfter(m)} allowClear={false} />
						<DatePicker size="large" value={before ?? null} onChange={(m) => m && setBefore(m)} allowClear={false} />
					</Space>
					<Button size="large" type="primary" icon={<CloseCircleOutlined />} onClick={() => setClientId(undefined)}>
						Wyczyść
					</Button>
				</div>

				<OrdersTable>
					<Table
						columns={columns}
						rowClassName={getRowClassName}
						dataSource={orders ?? []}
						loading={!orders}
						pagination={false}
						scroll={{ x: 'max-content' }}
					/>
				</OrdersTable>
			</div>
		</Page>
	);
};
